/**
 * trackingInsights.js - what the Tracking screens say ABOUT the numbers
 * (10/14/26): the warnings, the Setup checklist, the plan window, the burn
 * forecast, the latest entries and the week helpers of the Log grid. PURE.
 *
 * Every money / hours figure itself is the server's (lib/trackingEngine.js
 * calcTracking - the tracking master's formulas); nothing here prices a day
 * again. The forecast divides what the engine says is left by the job's
 * RECENT PACE - what was sold (labour + billed expenses) in the last four
 * weeks of logged days. Not the workbook's weekly burn (MASTER S4): S4 adds
 * every engineer who ever worked the job as if all of them were on site 7
 * days a week, a full-crew rate that ran ~4x the real pace on P26013 (10/14/26).
 */
import { localDay } from '../../utils/localDay';
import { fmtCost as fmtM, fmtHours as fmtH } from '../../utils/format';

const DAY_MS = 86400000;
const isDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
const utc = (iso) => Date.parse(`${iso}T00:00:00Z`);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const lc = (s) =>
  String(s || '')
    .trim()
    .toLowerCase();

/** 'YYYY-MM-DD' of a Date in local time (what the person's calendar says). */
export function isoOf(d) {
  return localDay(d);
}
export const addDays = (iso, n) => new Date(utc(iso) + n * DAY_MS).toISOString().slice(0, 10);
export const daysBetween = (a, b) => Math.round((utc(b) - utc(a)) / DAY_MS);
/** The Monday of the week a day is in. */
export function mondayOf(iso) {
  const g = new Date(utc(iso)).getUTCDay();
  return addDays(iso, g === 0 ? -6 : 1 - g);
}
/** The 7 days Monday..Sunday of the week starting `monday`. */
export const weekDays = (monday) => Array.from({ length: 7 }, (_, i) => addDays(monday, i));
/** "Mon" */
export const dowOf = (iso) => new Date(utc(iso)).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
/** "Aug 3" / "Aug 3, 2026". */
export function shortDate(iso, withYear = false) {
  if (!isDay(iso)) return '-';
  return new Date(utc(iso)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: withYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  });
}

/**
 * The budget's planned window: the first and last day the budget worksheet
 * has hours on (Setup > Budget), or null without a budget / without days.
 */
export function planWindow(data) {
  const ws = data && data.budget && data.budget.data && data.budget.data.worksheet;
  const days = [];
  for (const e of (ws && ws.engineers) || []) {
    for (const [d, r] of Object.entries(e.rows || {})) if (isDay(d) && num(r && r.h) > 0) days.push(d);
  }
  if (!days.length) return null;
  days.sort();
  return { start: days[0], end: days[days.length - 1] };
}

/** What the job is measured against: the budget's sold (Setup > Budget), else the Quoted figure. */
export function budgetSoldOf(data) {
  const B = data && data.budget && data.budget.totals;
  if (B && num(B.sold) > 0) return { value: num(B.sold), from: 'budget' };
  const q = data && data.tracking && data.tracking.quoted;
  if (q && num(q.sold) > 0) return { value: num(q.sold), from: 'quoted' };
  return { value: 0, from: null };
}

/**
 * Progress: share of the hours budget used, of the sold budget used, of the
 * planned time gone by `today`.
 */
export function progressOf(data, today) {
  const t = data.tracking;
  const cs = t.customerSummary;
  const budgetHours = num(cs.budget.nt) + num(cs.budget.ot) + num(cs.budget.dt);
  const usedHours = num(t.totals.hours);
  const sold = budgetSoldOf(data);
  const win = planWindow(data);
  let timePct = null;
  if (win && isDay(today)) {
    const span = Math.max(1, daysBetween(win.start, win.end) + 1);
    timePct = Math.min(1, Math.max(0, (daysBetween(win.start, today) + 1) / span));
  }
  return {
    budgetHours,
    usedHours,
    hoursPct: budgetHours > 0 ? usedHours / budgetHours : null,
    budgetSold: sold.value,
    soldFrom: sold.from,
    soldNow: num(t.totals.soldTotal),
    soldPct: sold.value > 0 ? num(t.totals.soldTotal) / sold.value : null,
    window: win,
    timePct,
  };
}

const PACE_WEEKS = 4;
/**
 * The job's recent pace: sold per week (labour + billed expenses with the
 * handling fee) over the last PACE_WEEKS weeks of logged days - counted back
 * from the latest logged day (or today, if earlier), and over fewer weeks when
 * the job is younger. Materials are left out (one-off buys, not a pace).
 * { perWeek, weeks, from, to } - perWeek 0 when nothing was logged.
 */
export function paceOf(data, today) {
  const rows = (data.tracking.rows || []).filter((r) => isDay(r.date));
  if (!rows.length) return { perWeek: 0, weeks: 0, from: null, to: null };
  const latest = rows.reduce((a, r) => (r.date > a ? r.date : a), rows[0].date);
  const first = rows.reduce((a, r) => (r.date < a ? r.date : a), rows[0].date);
  const to = isDay(today) && today < latest ? today : latest;
  let from = addDays(to, -(PACE_WEEKS * 7 - 1));
  if (from < first) from = first;
  const fee = num(data.tracking.totals.feeRate);
  const sold = rows.filter((r) => r.date >= from && r.date <= to).reduce((a, r) => a + num(r.sell) + num(r.expenseTotal) * (1 + fee), 0);
  const weeks = Math.max(1, Math.round((utc(to) - utc(from)) / DAY_MS + 1) / 7);
  return { perWeek: sold / weeks, weeks, from, to };
}

/**
 * Forecast to complete at the recent pace (paceOf): the sold budget left /
 * sold per week = weeks left, counted from today. `burn` is that pace;
 * `fullCrew` is the workbook's weekly burn (MASTER S4) for reference.
 * { state: 'nobudget' | 'noburn' | 'over' | 'ok', left, weeks, date }
 */
export function forecastOf(data, today) {
  const p = progressOf(data, today);
  const pace = paceOf(data, today);
  const burn = pace.perWeek;
  const fullCrew = num(data.tracking.totals.weeklyBurn);
  const base = { burn, pace, fullCrew };
  if (!(p.budgetSold > 0)) return { ...base, state: 'nobudget' };
  const left = p.budgetSold - p.soldNow;
  if (left <= 0) return { ...base, state: 'over', left };
  if (!(burn > 0)) return { ...base, state: 'noburn', left };
  const weeks = left / burn;
  const date = isDay(today) ? addDays(today, Math.round(weeks * 7)) : null;
  const late = !!(p.window && date && date < p.window.end);
  return { ...base, state: 'ok', left, weeks, date, runsOutBeforeEnd: late, planEnd: p.window ? p.window.end : null };
}

/** The latest day rows, newest first. */
export function recentOf(data, n = 8) {
  return [...(data.tracking.rows || [])].sort((a, b) => String(b.date).localeCompare(String(a.date)) || num(b.id) - num(a.id)).slice(0, n);
}

/**
 * What needs someone's attention: [{ id, level: 'error' | 'warn', text, go }]
 * (`go` = the Tracking view that fixes it: 'log', 'setup:budget',
 * 'setup:po', 'setup:people').
 */
export function warningsOf(data, seesCosts) {
  const t = data.tracking;
  const out = [];
  const cs = t.customerSummary;
  const hasBudget = num(cs.budget.nt) + num(cs.budget.ot) + num(cs.budget.dt) > 0;
  if (hasBudget) {
    for (const [k, label] of [
      ['nt', 'NT'],
      ['ot', 'OT'],
      ['dt', 'DT'],
    ]) {
      if (num(cs.remaining[k]) < 0)
        out.push({
          id: `over-${k}`,
          level: 'error',
          text: `${label} hours are ${fmtH(-cs.remaining[k])} over budget (${fmtH(cs.actual[k])} logged of ${fmtH(cs.budget[k])}).`,
          go: 'setup:budget',
        });
    }
  }
  if (num(cs.budget.expenses) > 0 && num(cs.remaining.expenses) < 0) {
    out.push({ id: 'over-exp', level: 'error', text: `Expenses are ${fmtM(-cs.remaining.expenses)} over budget.`, go: 'setup:budget' });
  }
  const sold = budgetSoldOf(data);
  if (sold.value > 0 && num(t.totals.soldTotal) > sold.value) {
    out.push({
      id: 'over-sold',
      level: 'error',
      text: `As sold (${fmtM(t.totals.soldTotal)}) is past the ${sold.from === 'budget' ? 'budget' : 'quoted'} ${fmtM(sold.value)}.`,
      go: 'setup:budget',
    });
  }
  for (const l of t.invoicing.lines || []) {
    if (!l.onPo) {
      out.push({
        id: `po-off-${l.label}`,
        level: 'warn',
        text: `"${l.label}" is used on days or materials but is not one of the PO lines.`,
        go: 'setup:po',
      });
      continue;
    }
    if (num(l.value) > 0 && num(l.remaining) < 0)
      out.push({
        id: `po-over-${l.label}`,
        level: 'error',
        text: `PO line ${l.label} is invoiced ${fmtM(-l.remaining)} past its value.`,
        go: 'setup:po',
      });
    else if (num(l.value) > 0 && num(l.remaining) === 0)
      out.push({
        id: `po-done-${l.label}`,
        level: 'warn',
        text: `PO line ${l.label} is fully invoiced - nothing left to bill on it.`,
        go: 'setup:po',
      });
    if (num(l.value) > 0 && num(l.sell) > num(l.value))
      out.push({
        id: `po-work-${l.label}`,
        level: 'error',
        text: `Work booked to PO line ${l.label} (${fmtM(l.sell)}) is more than the line's value (${fmtM(l.value)}).`,
        go: 'setup:po',
      });
  }
  if (num(t.invoicing.noLine && t.invoicing.noLine.hours) > 0 && (data.poLines || []).length) {
    out.push({ id: 'no-line', level: 'warn', text: `${fmtH(t.invoicing.noLine.hours)} hours are logged without a PO line.`, go: 'log' });
  }
  if ((t.unlisted || []).length) {
    out.push({
      id: 'unlisted',
      level: 'error',
      text: `${t.unlisted.length} day(s) have a task that is not on the tracking sheet's list and count nowhere.`,
      go: 'log',
    });
  }
  const people = data.people || [];
  const worked = new Set((t.people || []).filter((p) => num(p.hours) > 0).map((p) => lc(p.name)));
  const noSell = people.filter((p) => !(num(p.sellRate) > 0) && worked.has(lc(p.name)));
  if (noSell.length)
    out.push({ id: 'no-sell', level: 'error', text: `No sell rate for ${names(noSell)} - their hours sell at $0.`, go: 'setup:people' });
  if (seesCosts) {
    const noCost = people.filter((p) => p.costKnown === false && p.costFromStaff !== false && !(num(p.costRate) > 0));
    if (noCost.length)
      out.push({
        id: 'no-cost',
        level: 'warn',
        text: `No cost rate for ${names(noCost)} - their hours cost $0, so the margin is too high.`,
        go: 'setup:people',
      });
  }
  return out;
}

/**
 * The Setup checklist: [{ id, label, state: 'done' | 'warn' | 'todo', note }]
 * for Budget, PO lines, People & rates.
 */
export function checklistOf(data, seesCosts) {
  const budget = data.budget
    ? {
        state: 'done',
        note: `${fmtM(data.budget.totals && data.budget.totals.sold)} sold, ${fmtH(data.budget.totals && data.budget.totals.hours)} hrs`,
      }
    : data.trackingSheet && data.trackingSheet.quoted
      ? { state: 'done', note: 'Quoted figures from the imported tracking sheet' }
      : { state: 'todo', note: 'Take it from the won quote, or start a blank one' };
  const lines = data.poLines || [];
  const derived = lines.some((l) => l.derived);
  const po = !lines.length
    ? { state: 'todo', note: 'Add the PO line items' }
    : derived
      ? { state: 'warn', note: 'The project PO is used as one line - split it into its line items' }
      : {
          state: 'done',
          note: `${lines.length} line${lines.length === 1 ? '' : 's'}, ${fmtM(lines.reduce((a, l) => a + num(l.value), 0))}`,
        };
  const people = data.people || [];
  const noSell = people.filter((p) => !(num(p.sellRate) > 0)).length;
  const noCost = seesCosts ? people.filter((p) => p.costKnown === false && p.costFromStaff !== false && !(num(p.costRate) > 0)).length : 0;
  const issues = [
    noSell && `${noSell} missing sell rate${noSell === 1 ? '' : 's'}`,
    noCost && `${noCost} missing cost rate${noCost === 1 ? '' : 's'}`,
  ].filter(Boolean);
  const ppl = !people.length
    ? { state: 'todo', note: 'Add the engineers on this job' }
    : issues.length
      ? { state: 'warn', note: issues.join(', ') }
      : { state: 'done', note: `${people.length} ${people.length === 1 ? 'person' : 'people'}` };
  return [
    { id: 'budget', label: 'Budget', ...budget },
    { id: 'po', label: 'PO lines', ...po },
    { id: 'people', label: 'People & rates', ...ppl },
  ];
}

const names = (list) =>
  list.length > 3
    ? `${list
        .slice(0, 3)
        .map((p) => p.name)
        .join(', ')} and ${list.length - 3} more`
    : list.map((p) => p.name).join(', ');
